import React, { useEffect, useState } from 'react';
import { ChevronRight, Landmark, Sparkles } from 'lucide-react';
import SmartImage from './SmartImage';
import { resolveImageUrl as resolveImageUrlUtil } from '../utils/urlResolver';
import { fetchPublicMuseumInsights } from '../utils/museumCoLearningStats';
import { enrichMuseumShare } from '../utils/museumShareAccess';
import PublicMuseumContributionsModal from './PublicMuseumContributionsModal';

export default function PublicMuseumContributions({
  uid,
  onShareClick,
  canAccessShare,
  /** 'stack' = full-width row below profile; 'split' = right column beside profile */
  layout = 'stack',
}) {
  const isSplit = layout === 'split';
  const sectionClass = isSplit
    ? 'flex min-h-full min-w-0 flex-1 flex-col justify-center border-t border-[rgba(245,203,92,0.28)] bg-[rgba(255,253,248,0.55)] px-5 py-6 sm:max-w-[min(100%,26rem)] md:border-l md:border-t-0 md:px-6 md:py-7 lg:px-8'
    : 'border-t border-[rgba(245,203,92,0.28)] bg-[rgba(255,253,248,0.55)] px-5 py-6 sm:px-8 sm:py-7 md:px-10';
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [checkingShareId, setCheckingShareId] = useState(null);

  useEffect(() => {
    if (!uid) {
      setInsights(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetchPublicMuseumInsights(uid)
      .then((data) => {
        if (!cancelled) setInsights(data);
      })
      .catch(() => {
        if (!cancelled) setInsights(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uid]);

  const handleShareSelect = async (share) => {
    const enriched = enrichMuseumShare(share);
    if (!enriched || !onShareClick) return;
    setModalOpen(false);
    setCheckingShareId(enriched.id);
    try {
      await onShareClick(enriched);
    } finally {
      setCheckingShareId(null);
    }
  };

  if (loading) {
    return (
      <div className={sectionClass}>
        <div className="h-4 w-40 animate-pulse rounded bg-[#efe2d0]/80" />
        <div className="mt-4 h-12 animate-pulse rounded-2xl bg-[#efe2d0]/60" />
      </div>
    );
  }

  if (!insights?.shares?.length) {
    return null;
  }

  const { totalContributions, shares } = insights;
  const previewShares = shares.slice(0, 4);

  return (
    <>
      <section className={sectionClass}>
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-[#F59E0B]" strokeWidth={2} aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9A7A55]">
              Artist Salon
            </p>
            <h2 className="mt-1 text-lg font-bold text-[#2F251B] sm:text-xl">Salon contributions</h2>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(245,203,92,0.45)] bg-[rgba(255,250,240,0.95)] px-3 py-1.5 text-xs font-semibold text-[#6B5948]">
            <Landmark className="h-3.5 w-3.5 text-[#F59E0B]" strokeWidth={2} aria-hidden />
            {totalContributions} salon share{totalContributions === 1 ? '' : 's'}
          </span>

          {/* Compact preview thumbnails — not a full stack */}
          <div className="flex -space-x-2">
            {previewShares.map((share, i) => {
              const cover = resolveImageUrlUtil(share.coverUrl) || share.coverUrl;
              const unlocked = canAccessShare
                ? canAccessShare(enrichMuseumShare(share) || share)
                : true;
              return (
                <span
                  key={share.id || share.key || i}
                  className="relative inline-block h-9 w-9 overflow-hidden rounded-full border-2 border-[#FFFDF8] bg-[#f4efe6] ring-1 ring-[rgba(245,203,92,0.35)]"
                  style={{ zIndex: previewShares.length - i }}
                >
                  {cover ? (
                    <span
                      className="block h-full w-full overflow-hidden"
                      style={
                        unlocked
                          ? undefined
                          : { filter: 'blur(10px) brightness(0.7)', transform: 'scale(1.15)' }
                      }
                    >
                      <SmartImage
                        src={cover}
                        alt=""
                        className="h-full w-full object-cover"
                        containerClassName="h-full w-full"
                      />
                    </span>
                  ) : (
                    <span className="flex h-full w-full items-center justify-center text-xs text-[#c9a574]">
                      A
                    </span>
                  )}
                </span>
              );
            })}
            {totalContributions > 4 ? (
              <span className="relative z-0 inline-flex h-9 w-9 items-center justify-center rounded-full border-2 border-[#FFFDF8] bg-[#f4efe6] text-[10px] font-semibold text-[#6B5948] ring-1 ring-[rgba(245,203,92,0.35)]">
                +{totalContributions - 4}
              </span>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setModalOpen(true)}
          disabled={checkingShareId}
          className={`mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-[rgba(245,203,92,0.5)] bg-gradient-to-r from-[#FFF9ED] to-[#FFF3DC] px-4 py-3.5 text-sm font-semibold text-[#2F251B] shadow-[0_8px_22px_rgba(120,72,20,0.08)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#F5CB5C] hover:shadow-[0_12px_28px_rgba(245,158,11,0.15)] disabled:opacity-70 ${
            isSplit ? '' : 'sm:w-auto sm:min-w-[280px]'
          }`}
        >
          View museum contributions
          <ChevronRight className="h-4 w-4 text-[#F59E0B]" strokeWidth={2.5} aria-hidden />
        </button>
      </section>

      {modalOpen ? (
        <PublicMuseumContributionsModal
          shares={shares}
          canAccessShare={canAccessShare}
          onShareSelect={handleShareSelect}
          onClose={() => setModalOpen(false)}
        />
      ) : null}
    </>
  );
}
